import {
    Icon,
    MessageBarType,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
} from '@fluentui/react';
import React, { FC, useEffect, useState,useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { ApiException, MessageBar } from '@eta/components';
import OTPInput from './OTPInput';
import { WizardStateAndLogic } from '../common/component/WizardPivot';
import { Defaults } from '../common/config/constants';
import { ServiceCreator } from './Service/client';
import { itemsKey } from './TaxpayerSelfRegistryMain';
import {
    CompleteSessionDigitalProfileDto,
    LanguageTypes,
    NewSessionResponse,
    ResendSessionOTPRequest,
    ValidationType,
} from './Service/TaxpayerProxy';
import './TaxpayerVerification.scss';

export type otpStatus = 'Verified' | 'NotVerified' | 'Invalid';

export interface TaxpayerVerificationProps {
    setFooter: (footer: JSX.Element) => void;
    taxpayerSelfRegistryWizard: WizardStateAndLogic;
    mobileNo: string;
    newSession: NewSessionResponse;
    defaultLanguage: LanguageTypes;
}

export const TaxpayerVerification: FC<TaxpayerVerificationProps> = (
    props: TaxpayerVerificationProps
) => {
    const { t } = useTranslation(['SignUp']);
    const oTPLength = 6;
    const [seconds, setSeconds] = useState(Defaults.ResendOTP);
    const {
        setFooter,
        taxpayerSelfRegistryWizard,
        mobileNo,
        newSession,
        defaultLanguage,
    } = props;
    const [canRegister, setCanRegister] = useState<boolean>(false);
    const [emailOTPStatus, setEmailOTPStatus] =
        useState<otpStatus>('NotVerified');
    const [mobileOTPStatus, setMobileOTPStatus] =
        useState<otpStatus>('NotVerified');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorrMsg, setErrorrMsg] = useState<string>();
    const client = ServiceCreator.getTaxpayerManagementPublicClient(true);

     const mobileOTP = useRef<string>();
    const emailOTP = useRef<string>();

    const resendOTP = async () => {
        try {
            setSeconds(Defaults.ResendOTP);
            if (mobileOTPStatus !== 'Verified')
                await client.resendSessionOtp(
                    newSession?.sessionId ?? '',
                    new ResendSessionOTPRequest({
                        preferredLanguage: defaultLanguage,
                        validationType: ValidationType.Mobile,
                    })
                );
            if (emailOTPStatus !== 'Verified')
                await client.resendSessionOtp(
                    newSession?.sessionId ?? '',
                    new ResendSessionOTPRequest({
                        preferredLanguage: defaultLanguage,
                        validationType: ValidationType.Email,
                    })
                );
        } catch {;}
    };

    const registerTaxPayer = async () => {
        try {
            setIsLoading(true);
            setErrorrMsg(undefined);
            const response = await client.completeSessionDigitalProfile(
                newSession?.sessionId ?? '',
                new CompleteSessionDigitalProfileDto({
                    otpEmail: emailOTP.current,
                    otpPhone: mobileOTP.current,
                })
            );
            if (response.id) {
                taxpayerSelfRegistryWizard.ChangeTabState(
                    itemsKey.Verification,
                    'completed'
                );
                taxpayerSelfRegistryWizard.nextTab();
            } else setErrorrMsg(t('PleaseCheckErrors'));
        } catch (ex) {
            taxpayerSelfRegistryWizard.ChangeTabState(
                itemsKey.Verification,
                'error'
            );
            const exception = ex as ApiException;
            setEmailOTPStatus(
                exception.serviceError?.getFieldError('Email')
                    ? 'Invalid'
                    : 'Verified'
            );
            setMobileOTPStatus(
                exception.serviceError?.getFieldError('Mobile')
                    ? 'Invalid'
                    : 'Verified'
            );
            setErrorrMsg(
                exception?.serviceError
                    ? exception?.serviceError.toString()
                    : exception.toString()
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setFooter(
            <PrimaryButton
                className="continue"
                menuIconProps={{ iconName: 'Forward' }}
                disabled={!canRegister || isLoading}
                text={t('RegisterTaxpayer')}
                onClick={() => registerTaxPayer()}
            >
                {isLoading && <Spinner size={SpinnerSize.small} />}
            </PrimaryButton>
        );
    }, [canRegister, t, isLoading]);

    useEffect(() => {
        setCanRegister(
            mobileOTP.current?.length === oTPLength && emailOTP?.current?.length === oTPLength
        );
    }, [mobileOTP.current, emailOTP.current]);

    useEffect(() => {
        setEmailOTPStatus('NotVerified');
        setMobileOTPStatus('NotVerified');
        setSeconds(Defaults.ResendOTP);
    }, [taxpayerSelfRegistryWizard.state.selectedTabKey]);

    const renderTimer = (time: number) => {
        let second = (time % 60).toString();
        let minute = Math.floor(time / 60).toString();
        minute =
            minute.toString().length === 1 ? `0${minute}` : minute.toString();
        second =
            second.toString().length === 1 ? `0${second}` : second.toString();
        return `${minute}:${second}`;
    };
    
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let intervalId: any ;
        if (
            seconds > 0 &&
            taxpayerSelfRegistryWizard.state.selectedTabKey === 'Verification'
        ) {
            intervalId= setInterval(() => setSeconds(seconds - 1), 1000);
        } 
        return () => clearInterval(intervalId);
    });

    return (
        <div className="SignUp_TaxpayerVerification">
            {errorrMsg && (
                <MessageBar
                    messageBarType={MessageBarType.error}
                    isMultiline={false}
                    className="messageBar"
                    onDismiss={() => {
                        setErrorrMsg(undefined);
                    }}
                >
                    {errorrMsg}
                </MessageBar>
            )}
            <div className="SignUp_TaxpayerVerificationContent">
                <div className="description">
                    <p>{t('VerifyContactDescription1')}</p>
                    <p>{t('VerifyContactDescription2')}</p>
                </div>
                <div className="grid-content">
                    <div className="leftContent">
                        <div className="emailVerification">
                            <span>{t('OneTimeCode')}</span>
                            <span className="bold">{t('EmailAddress')}</span>
                        </div>

                        <div className="innerContent">
                            <div className="relative">
                                <img
                                    className="noDraftTempalteImage"
                                    src="/Email.svg"
                                    alt="Email"
                                />
                                {emailOTPStatus === 'Verified' && (
                                    <div className="valid">
                                        <Icon iconName="Accept" />
                                    </div>
                                )}
                            </div>
                            <div>
                                <span className="emailCode">
                                    {t('EmailCode')}
                                </span>
                                <div className="otpContainer">
                                    <OTPInput
                                        autoFocus
                                        isNumberInput
                                        length={oTPLength}
                                        inputClassName={clsx(
                                            'otpInput',
                                            emailOTPStatus === 'Invalid'
                                                ? 'otpborderErrorr'
                                                : 'otpborder',
                                            emailOTPStatus === 'Verified'
                                                ? 'otpVerified'
                                                : ''
                                        )}
                                        onChangeOTP={(otp) => { emailOTP.current =otp;}}
                                    />
                                    <br />
                                    {emailOTPStatus === 'Invalid' && (
                                        <p className="invalid_otp">
                                            {t('codeNotVerified')}
                                        </p>
                                    )}
                                </div>
                                <span className="resendOTP">
                                    {t('ResendCode')}
                                    {seconds > 0 ? (
                                        <span className="font_Timer">
                                            {' '}
                                            {renderTimer(seconds)}{' '}
                                        </span>
                                    ) : (
                                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                        <a
                                            href={undefined}
                                            className="Link_Button"
                                            onClick={() => {
                                                setSeconds(Defaults.ResendOTP);
                                                resendOTP();
                                            }}
                                        >
                                            {t('resend')}
                                        </a>
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>

                    <Separator vertical />

                    <div className="rightContent">
                        <div className="phoneVerification">
                            <span>{t('OneTimeCode')}</span>
                            <span>{t('MobilePhone')}</span>
                            <p className="bold">
                                *****
                                {mobileNo?.slice(mobileNo.length - 8)}
                            </p>
                        </div>
                        <div className="innerContent">
                            <div className="relative">
                                <img
                                    className="noDraftTempalteImage"
                                    src="/Mobile.svg"
                                    alt="Mobile"
                                />
                                {mobileOTPStatus === 'Verified' && (
                                    <div className="valid">
                                        <Icon iconName="Accept" />
                                    </div>
                                )}
                            </div>
                            <div>
                                <span>{t('MobileCode')}</span>
                                <div className="otpContainer">
                                    <OTPInput
                                        autoFocus
                                        isNumberInput
                                        length={oTPLength}
                                        inputClassName={clsx(
                                            'otpInput',
                                            mobileOTPStatus === 'Invalid'
                                                ? 'otpborderErrorr'
                                                : 'otpborder',
                                            mobileOTPStatus === 'Verified'
                                                ? 'otpVerified'
                                                : ''
                                        )}
                                        onChangeOTP={(otp) => {mobileOTP.current=otp;}}
                                    />
                                    <br />
                                    {mobileOTPStatus === 'Invalid' && (
                                        <p className="invalid_otp">
                                            {t('codeNotVerified')}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
